// Settlement status descriptions for clarity:
// - 'pending': Awaiting action from recipient
// - 'completed': Successfully confirmed by recipient
// - 'rejected': Declined by recipient (disagrees with settlement)
// - 'cancelled': Withdrawn by sender (changed their mind)
// Import NotificationType from centralized constants
import type { GROUP_CATEGORIES } from '../constants/group-categories';
import type { NotificationType } from '../constants/notification-types';

export interface NotificationPreferences {
  expenseAdded: boolean;
  expenseUpdated: boolean;
  settlementCreated: boolean;
  settlementCompleted: boolean;
  groupInvitation: boolean;
  paymentReminder: boolean;
  emailNotifications: boolean;
  pushNotifications: boolean;
}

export interface User {
  id: string;
  email: string;
  name: string;
  password: string;
  avatar?: string;
  phone?: string;
  notificationPreferences?: NotificationPreferences;
  createdAt: Date;
  updatedAt: Date;
}

export interface Group {
  id: string;
  name: string;
  description?: string;
  members: GroupMember[];
  category: GroupCategory;
  simplifyDebts: boolean;
  defaultCurrency?: string;
  budget?: number;
  categorySettings?: GroupCategorySettings;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface GroupMember {
  userId: string;
  user: User;
  joinedAt: Date;
}

export type GroupCategory = (typeof GROUP_CATEGORIES)[number];

export interface SettlementReminder {
  enabled: boolean;
  frequency: 'weekly' | 'biweekly' | 'monthly';
  lastReminder?: Date;
  nextReminder?: Date;
}

export interface GroupCategorySettings {
  // Semantic dates - meaning depends on group category:
  // Travel: trip start/end dates
  // Events: event date (uses startDate only)
  // Roommates: lease start/end dates
  // Work: project start/end dates
  startDate?: Date;
  endDate?: Date;
  // Settlement reminders for all categories
  settlementReminder?: SettlementReminder;
}

export type SplitMethod = 'equal' | 'percentage' | 'custom' | 'shares';

export type LineItemsRestMode = 'proportional' | 'equal';

export interface LineItemsRest {
  mode: LineItemsRestMode;
}

/** The group's default split, copied onto an itemized expense at create: how unpicked units (and legacy rows with no sharer in the split) are shared. `weights` maps userId → percentage or share count; absent, empty or `equal` = equal. */
export interface UnpickedSplit {
  method: 'equal' | 'percentage' | 'shares';
  weights: Record<string, number>;
}

/** A participant who said "That's mine" on an itemized expense, even with nothing ticked. A later pick keeps the first `pickedAt`. */
export interface ExpensePick {
  userId: string;
  pickedAt: string;
  source: 'app' | 'link';
}

export interface LineItemPick {
  userId: string;
  /** Whole units of the row this person took; ≥ 1. */
  units: number;
}

/** Amounts are in the line-item currency (`originalCurrency ?? currency`). `sharedBy` is the pickers; a row without `picks` was saved before per-unit picks and splits equally among its sharers, where an empty `sharedBy` means every participant shares it. */
export interface ExpenseLineItem {
  id: string;
  name: string;
  amount: number;
  quantity: number;
  sharedBy: string[];
  picks?: LineItemPick[];
}

export interface ExpensePayer {
  userId: string;
  amount: number;
}

export interface Expense {
  id: string;
  groupId: string;
  title: string;
  description?: string;
  amount: number;
  currency: string;
  category: ExpenseCategory;
  paidBy: string | ExpensePayer[]; // Support both single payer (backward compat) and multiple payers
  splits: ExpenseSplit[];
  splitMethod: SplitMethod;
  receipt?: string;
  date: Date;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface ExpenseSplit {
  userId: string;
  amount: number;
  percentage?: number;
  shares?: number; // Per-expense share override (used when splitMethod is 'shares')
}

export type ExpenseCategory =
  | 'food'
  | 'drinks'
  | 'transport'
  | 'accommodation'
  | 'entertainment'
  | 'utilities'
  | 'shopping'
  | 'healthcare'
  | 'education'
  | 'other';

export interface Settlement {
  id: string;
  groupId: string;
  fromUserId: string;
  toUserId: string;
  amount: number;
  currency: string;
  method: PaymentMethod;
  status: SettlementStatus;
  transactionId?: string;
  note?: string;
  createdAt: Date;
  updatedAt: Date;
}

export type PaymentMethod =
  'cash' | 'paypal' | 'venmo' | 'stripe' | 'bank_transfer';

// Payment methods offered in the record-payment pickers (web + mobile).
// Cash first (most common), then bank transfer, then the rest a-z.
// ('stripe' is a legacy stored value, never offered for new payments.)
export const PAYMENT_METHODS = [
  'cash',
  'bank_transfer',
  'other',
  'paypal',
  'venmo',
] as const;

export type SettlementStatus =
  'pending' | 'completed' | 'rejected' | 'cancelled';

// Settlement status enum for type-safe usage
export const SETTLEMENT_STATUS = {
  PENDING: 'pending',
  COMPLETED: 'completed',
  REJECTED: 'rejected',
  CANCELLED: 'cancelled',
} as const;

export interface Notification {
  id: string;
  userId: string;
  type: NotificationType;
  title: string;
  message: string;
  data?: Record<string, unknown>;
  read: boolean;
  createdAt: Date;
}

// Re-export NotificationType for backward compatibility
export type { NotificationType };

export interface Balance {
  userId: string;
  groupId: string;
  currency: string;
  amount: number;
  owes: Array<{
    toUserId: string;
    amount: number;
  }>;
  owed: Array<{
    fromUserId: string;
    amount: number;
  }>;
}

// Re-export DTOs for easy access
export * from './dtos';

// Re-export offline types
export * from './offline';
